import { NgClass } from '@angular/common';
import { Component, ComponentRef, ElementRef, EventEmitter, Output, Type, ViewChild, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { PlatformHelper } from 'src/app/core/helpers/platform.helper';

import { ModalRef } from './modal-ref';
import { ModalComponent } from './modal.component';
import { ModalContentHostDirective } from './modal.directive';
import { ModalInjector } from './modal.injector';
import { ModalOptions } from './modal.model';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'modal-container',
  templateUrl: './modal-container.component.html',
  imports: [ModalContentHostDirective, NgClass]
})
export class ModalContainerComponent {
  private platformHelper = inject(PlatformHelper);

  @ViewChild(ModalContentHostDirective, { static: true })
  modalContentHost: ModalContentHostDirective;
  @ViewChild('modalContainer', { static: true }) modalContainer: ElementRef<HTMLElement>;
  @Output() modalClose = new EventEmitter<any>();

  id: string;
  modalRef?: ModalRef;
  component: ComponentRef<any>;
  options?: ModalOptions;
  startHeight = 0;
  currentHeight = 0;

  closed = false;
  animateModal: boolean;

  destroy$ = new Subject();

  open(options: ModalOptions, component?: Type<any>, componentInputs?: unknown): ModalRef {
    this.modalRef = this.renderComponent(options, component, componentInputs);
    this.options = options;

    return this.modalRef;
  }

  close(): void {
    this.modalClose.emit();
    if (this.modalRef) {
      this.modalRef.close();
    }
    this.closed = true;
  }

  onAnimationModalDone(event?: AnimationEvent): void {
    if (event && event.animationName !== 'modal-leave') {
      return;
    }

    if (this.component && this.closed) {
      this.component.destroy();
      this.modalRef = undefined;
    }
  }

  onUpdate(): void {
    this.startHeight = this.modalContainer.nativeElement.scrollHeight;
    this.animateModal = !this.animateModal;
  }

  onBackdropClick(): void {
    if (this.options?.disableBackdropClick || this.options?.disableDefaultClose || this.platformHelper.isMobile) {
      return;
    }

    this.close();
  }

  private renderComponent(options: ModalOptions, component?: Type<any>, componentInputs?: unknown): ModalRef {
    // Cleanup previous listeners
    this.destroy$.next(void 0);

    // add options to Map (unique values)
    const map = new WeakMap();
    map.set(ModalOptions, options);

    // add the ModalRef for dependency injection
    const modalRef = new ModalRef();
    map.set(ModalRef, modalRef);

    // Subscribe to close call from component
    modalRef.onClose$.pipe(takeUntil(this.destroy$), take(1)).subscribe(() => {
      this.close();
    });

    modalRef.onUpdate$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onUpdate();
    });

    // Store current height for modal animation
    this.startHeight = this.modalContainer.nativeElement.clientHeight;

    const viewContainerRef = this.modalContentHost.viewContainerRef;
    viewContainerRef.clear();

    this.component = viewContainerRef.createComponent(component || ModalComponent, {
      injector: new ModalInjector(viewContainerRef.injector, map)
    });

    if (componentInputs) {
      for (const key of Object.keys(componentInputs)) {
        ((this.component.instance as Type<any>)[key] as unknown) = (componentInputs as Record<string, unknown>)[key];
      }
    }

    modalRef.componentInstance = this.component.instance;
    modalRef.options = options;

    // Perform update call
    this.onUpdate();

    return modalRef;
  }
}
